import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { alertProgressMsg, alertSuccessMsg, executeStored } from '../Utils';
import useSettings from '../hooks/useSettings';
import useUserData from '../hooks/useUserData';
import Form from '../Form';
import ModuleSearchField from '../fields/ModuleSearchField';

interface AddToCampaignDialogProps {
	/** The model instance  */
	model: any;
	/** Function used to refresh the related view */
	updateRefresh: () => void;
	/** The module name */
	module: string;
	/** The destination table name */
	table: string;
}

export function showAddToCampaignDialog(props: AddToCampaignDialogProps) {
	return openDialog({
		children: <AddToCampaignDialog {...props} />,
		fullWidth: true,
		maxWidth: 'sm',
		onClose: () => {}
	});
}

const getFiltersForModule = (module: string) => {
	switch (module) {
		case 'marketingactions':
			return [
				{
					filt_field: 'maac_mast_id',
					filt_clause: 'in',
					filt_value: [99, 2] // Bozza e In corso
				}
			];
		case 'commercialcampaigns':
			return [
				{
					filt_field: 'cmcp_cmst_id',
					filt_clause: 'in',
					filt_value: [5, 1] // Bozza e In corso
				}
			];
		case 'dems':
			return [
				{
					filt_field: 'dems_dmst_id',
					filt_clause: 'equals',
					filt_value: 1 // Bozza
				}
			];
		default:
			return [];
	}
};

/** This dialog add items to dems campaigns or marketing actions campaign */
export default function AddToCampaignDialog({ model, updateRefresh, module, table }: AddToCampaignDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(module);
	const { settings: tableSettings } = useSettings(table);
	const userData = useUserData();

	const [selectedCampaign, setSelectedCampaign] = React.useState<string | null>(null);

	const submit = () => {
		dispatch(alertProgressMsg(t));

		const filterPrefix = module === 'dems' ? 'derc' : module === 'marketingactions' ? 'marw' : 'cmrw';

		executeStored({
			action: `st_${
				module === 'dems'
					? 'dems_recipients'
					: module === 'marketingactions'
						? 'marketingactions_rows'
						: 'commercialcampaigns_rows'
			}_add_multiple`,
			data: {
				filters: [
					{
						filt_field: `${tableSettings.modu_short}_uid`,
						filt_clause: 'equals',
						filt_value: model[`${tableSettings.modu_short}_uid`]
					}
				],
				[`${filterPrefix}_user_created_uid`]: userData.user_uid,
				[`${filterPrefix}_table`]: table,
				[`${filterPrefix}_${settings.modu_short}_uid`]: selectedCampaign,
				[`${filterPrefix}_table_uid`]: model[`${tableSettings.modu_short}_uid`]
			},
			dispatch,
			successHandler: () => {
				dispatch(closeDialog());
				dispatch(alertSuccessMsg(t));
				updateRefresh();
			}
		});
	};

	return (
		<>
			<DialogTitle>{t('DIALOG_TITLE_ADD_TO')}</DialogTitle>
			<DialogContent>
				<Form id="addToCampaignDialog" onSubmit={submit}>
					<Grid container>
						<Grid size={12}>
							<ModuleSearchField
								name={`${settings.modu_short}_title`}
								label={t(`MODULE_${module}_NAME_SINGULAR`)}
								uid={selectedCampaign}
								module={module}
								filters={getFiltersForModule(module)}
								isEdit
								sendChange={(_e, v) => setSelectedCampaign(v)}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="addToCampaignDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
